import React from 'react'

import PropTypes from 'prop-types'
import clsx from 'clsx'
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  FormControlLabel,
  Switch,
} from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'

import { LoadingFeedback } from 'components'

import useSnackbar from 'hooks/useSnackbar'
import useSupplierInvite from 'hooks/useSupplierInvite'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'
import schema from './schema'
import useStyles from './styles'
import theme from 'theme'

const ConfirmDocumentForm = ({ inviter, companyToken }) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { setCurrentStep, isLoading, setIsLoading } = useSupplierInvite()

  const { mixpanel } = helpers

  const { control, handleSubmit, errors, watch } = useForm({
    validationSchema: schema,
    defaultValues: {
      document: '',
      isInternational: false,
    },
  })

  const onSubmit = async (formData) => {
    setIsLoading(true)

    mixpanel.track('Convite de Fornecedores', 'Confirme seu documento', {
      action: 'Avançar-Confirme_seu_documento',
    })

    const strippedDocument = formData.isInternational
      ? (formData.document || '').trim()
      : helpers.regex.onlyNumbersAndLetters(formData.document)

    try {
      // A busca na API procura por company supplier com esse documento
      const response = await service.dponet.supplierInvite.checkInvite(
        {
          supplierInvite: {
            document: strippedDocument,
          },
        },
        companyToken,
      )

      const supplierPortalLink = response?.data

      // Redireciona para o novo portal com o documento preenchido na primeira etapa
      const url = new URL(supplierPortalLink)
      url.searchParams.set('document', strippedDocument)
      window.location.href = url.toString()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Ocorreu um erro ao confirmar o seu documento',
        variant: 'error',
      })
      setCurrentStep(constants.supplierInvite.NOT_LINKED_STEP)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <LoadingFeedback open={isLoading} />
      {!isLoading && (
        <Grid container justifyContent="center" alignItems="center">
          <Box className={classes.groupTypography}>
            <Typography className={clsx(classes.text, classes.textWelcome)}>
              Vamos verificar seu cadastro!
            </Typography>
            <Typography className={classes.text}>
              Informe seu documento para confirmarmos seu vínculo com{' '}
              <span className={classes.highlight}>{inviter?.name}</span>
            </Typography>
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={theme.spacing(0.5)}>
              <Grid item xs={12}>
                <Box mt={theme.spacing(0.5)}>
                  <Box
                    display="flex"
                    justifyContent="left"
                    alignItems="center"
                    my={theme.spacing(0.5)}
                  >
                    <Box mr={theme.spacing(0.5)}>
                      <Typography
                        variant="body1"
                        className={classes.labelOption}
                      >
                        Nacional (Brasil)
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Controller
                          as={<Switch color="secondary" />}
                          name="isInternational"
                          control={control}
                        />
                      }
                      labelPlacement="end"
                    />
                    <Typography variant="body1" className={classes.labelOption}>
                      Internacional
                    </Typography>
                  </Box>
                  <Controller
                    as={
                      <TextField
                        className={classes.input}
                        type="text"
                        error={!!errors.document}
                        helperText={errors?.document?.message}
                        fullWidth
                        placeholder={
                          watch('isInternational')
                            ? 'Digite o registro da empresa'
                            : 'CNPJ ou CPF'
                        }
                      />
                    }
                    name="document"
                    onChange={([e]) => {
                      if (watch('isInternational')) {
                        return e.target.value
                      }
                      return helpers.formatters.document.identify(
                        e.target.value,
                        true,
                      )
                    }}
                    mode="onChange"
                    control={control}
                    autoFocus
                  />
                </Box>
              </Grid>
            </Grid>
            <Box mt={4} mb={2}>
              <Button
                className={classes.roundedButton}
                type="submit"
                disabled={isLoading}
                fullWidth
              >
                Procurar
              </Button>
            </Box>
          </form>
        </Grid>
      )}
    </>
  )
}

ConfirmDocumentForm.propTypes = {
  inviter: PropTypes.object,
  companyToken: PropTypes.string,
}

export default ConfirmDocumentForm
